const ALLOWED_TYPES = new Set(['image/jpeg', 'image/png'])
const MAX_BYTES = 25 * 1024 * 1024
const MIN_DIMENSION = 100

export type FlatImageValidationResult =
  | { ok: true; contentType: string }
  | { ok: false; message: string }

const extensionToContentType = (file: File): string | null => {
  const name = file.name.toLowerCase()
  if (name.endsWith('.png')) return 'image/png'
  if (name.endsWith('.jpg') || name.endsWith('.jpeg')) return 'image/jpeg'
  return null
}

export const validateFlatImageFile = (
  file: File,
): Promise<FlatImageValidationResult> => {
  if (file.size > MAX_BYTES) {
    return Promise.resolve({
      ok: false,
      message: 'Image must be 25 MB or smaller.',
    })
  }

  const contentType =
    (file.type && ALLOWED_TYPES.has(file.type) ? file.type : null) ??
    extensionToContentType(file)

  if (!contentType) {
    return Promise.resolve({
      ok: false,
      message: 'Only JPEG and PNG images are allowed.',
    })
  }

  return new Promise(resolve => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      if (img.width < MIN_DIMENSION || img.height < MIN_DIMENSION) {
        resolve({
          ok: false,
          message: `Image must be at least ${MIN_DIMENSION}×${MIN_DIMENSION} pixels.`,
        })
        return
      }
      resolve({ ok: true, contentType })
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      resolve({ ok: false, message: 'Could not read image file.' })
    }
    img.src = url
  })
}
